import {
  DragAndDropFiles,
  IStyles,
  InstructionsPanel,
  Loader,
  NumericalInputField,
  PrimaryButton,
  getFileExtensionType,
  getFileSizeInMB,
  getFileType,
  getImageType,
  getLocalStorage,
  getMediaBasePath,
  pxToRem,
  pxTovW,
  uploadFile,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  FileEnum,
  Module_ModuleCategoryEnum,
} from '@protos/content_management/content.db_pb';
import { FileUploadPurpose } from '@protos/learning_management/lms.common.apis_pb';
import {
  ChapterIdsInfo,
  CloudUpload,
  SessionModeEnum,
  SubmissionType,
  TaskCreationStatusEnum,
  TaskEnum,
} from '@protos/learning_management/lms.db_pb';
import { AssessmentCreationSubmitV6Request } from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { ChangeEvent, DragEvent, useEffect, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useGlobalContext } from '../../../app/Context/GlobalContextProvider';
import { useAppDispatch, useDeserifiedSelector } from '../../../reduxStore/reduxHooks';
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';
import { setResourceRedirectionPath } from '../../Teach/reducer/teach.slice';
import { CustomUploadFileResponse } from '../../Assessment/reducer/assessmentFlow.slice';
import { onResourceClick } from '../../../utils/resource';
import { LEARNING_HOMEWORK_ASSIGN } from '../../../routeHandling/RoutesNomenclature';
import ResourcePicker from './components/ResourcePicker';
import { getLearningHwErrorMessage } from '../utils';

// Duplicated from Assessment/Homework/CreateCustomHomework (demo only).
// Submission is always ONLINE; the draft id lives in the URL (?assessmentId=)
// so the form survives resource previews, the assign page's back button and reloads.
const DEFAULT_INSTRUCTIONS =
  '<ul><li>Complete all the assigned resources.</li><li>Upload your homework before the deadline.</li></ul>';

const styles: IStyles = {
  root: {
    padding: {
      xs: `${pxToRem(0)} ${pxToRem(20)} ${pxToRem(80)}`,
      md: `${pxTovW(25)} ${pxTovW(40)}`,
      lg: `${pxTovW(25)} ${pxTovW(240)}`,
    },
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(20), md: pxTovW(30) },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    pt: { xs: pxToRem(20), md: 0 },
  },
  contentBox: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: { xs: pxToRem(20), md: pxTovW(40) },
  },
  marksBox: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(20), md: pxTovW(15) },
    mt: { xs: pxToRem(20), md: pxTovW(30) },
  },
};

export const LearningHwCreate = () => {
  const {
    LmsCommonAPIServiceV2ClientWithStatusCodeHandler,
    LmsAssessmentTeacherAPIServiceV6WithStatusCodeHandler,
    LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const { setSelectedFunction } = useGlobalContext();
  const { subject_id, chapter_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const teacher_id = getLocalStorage('userId');
  const class_subject_info = useDeserifiedSelector(
    (state) => state.homeDashboard.class_subject_info
  );

  const draftIdValue = searchParams.get('assessmentId');
  const draftIdParam =
    draftIdValue && /^\d+$/.test(draftIdValue)
      ? BigInt(draftIdValue)
      : undefined;
  const [assessmentId, setAssessmentId] = useState<bigint | undefined>(
    draftIdParam
  );
  const [restoring, setRestoring] = useState(!!draftIdParam);
  const [saving, setSaving] = useState(false);
  const [instructionsValue, setInstructionsValue] = useState<string>();
  const [marks, setMarks] = useState<number | undefined>();
  // "Resources only" is stored as SUBMISSION_OFFLINE: the server requires a
  // file only for SUBMISSION_ONLINE, and existing teacher screens handle OFFLINE.
  const [uploadRequired, setUploadRequired] = useState(true);
  const [userUploads, setUserUploads] = useState<CustomUploadFileResponse[]>(
    []
  );
  const [selectedResourceIds, setSelectedResourceIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedFunction(() => () => navigate(-1));
    return () => {
      setSelectedFunction(null);
    };
  }, []);

  // Restore a saved draft (after preview, back from assign, or reload).
  useEffect(() => {
    if (!draftIdParam) return;
    (async () => {
      try {
        const response =
          await LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchAssessmentContent(
            {
              personId: teacher_id,
              personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
              assessmentId: draftIdParam,
            }
          );
        const assessment = response?.data?.assessment;
        if (assessment) {
          setInstructionsValue(assessment.assessmentInstructions?.[0]);
          setMarks(assessment.maxMarks || undefined);
          setUploadRequired(
            assessment.submissionType !== SubmissionType.SUBMISSION_OFFLINE
          );
          setSelectedResourceIds(assessment.taskMetaInfo?.resourceIds ?? []);
          setUserUploads(
            assessment.taskUploads
              .filter((upload) => !upload.isDeleted)
              .map((upload) => ({
                uploadFileData: upload,
                fileUploadStatus: 'success',
                fileSize: upload.fileSizeInMb || 0,
              }))
          );
        }
      } catch (err) {
        dispatch(
          setToastInfo({
            label: getLearningHwErrorMessage(err, 'Could not load the draft'),
            variant: 'error',
            open: true,
          })
        );
      } finally {
        setRestoring(false);
      }
    })();
  }, []);

  const buildRequest = (status: TaskCreationStatusEnum, draftId?: bigint) =>
    new AssessmentCreationSubmitV6Request({
      teacherId: teacher_id,
      subjectId: Number(subject_id),
      sectionId: class_subject_info?.sectionId,
      taskName: 'Learning Homework',
      moduleId: Number(chapter_id),
      category: Module_ModuleCategoryEnum.MODULE_CATEGORY_CHAPTER,
      chapterIdsInfo: [new ChapterIdsInfo({ chapterId: Number(chapter_id) })],
      selectedTasksInfo: [],
      instructions: [instructionsValue || DEFAULT_INSTRUCTIONS],
      creationStatus: status,
      taskType: TaskEnum.TASK_HOMEWORK_TEACHER_CUSTOM,
      submissionType: uploadRequired
        ? SubmissionType.SUBMISSION_ONLINE
        : SubmissionType.SUBMISSION_OFFLINE,
      taskUploads: userUploads
        .filter((upload) => upload.fileUploadStatus === 'success')
        .map((upload) => upload.uploadFileData),
      maxMarks: marks ?? 0,
      assessmentId: draftId ?? assessmentId,
      // The server overwrites resource ids on every save, drafts included.
      resourceIds: selectedResourceIds,
    });

  // Saves and pins the id in the URL so this page can be restored.
  const save = async (status: TaskCreationStatusEnum) => {
    const response =
      await LmsAssessmentTeacherAPIServiceV6WithStatusCodeHandler.assessmentCreationSubmit(
        buildRequest(status)
      );
    const savedId = response?.data?.assessment?.assessmentId;
    if (!savedId) throw new Error('No assessment returned');
    setAssessmentId(savedId);
    setSearchParams({ assessmentId: savedId.toString() }, { replace: true });
    return savedId;
  };

  const toastError = (err: unknown, fallback: string) => {
    console.error(err);
    dispatch(
      setToastInfo({
        label: getLearningHwErrorMessage(err, fallback),
        variant: 'error',
        open: true,
      })
    );
  };

  const handleProceedClick = async () => {
    if (!class_subject_info?.sectionId) return;
    if (selectedResourceIds.length === 0) {
      dispatch(
        setToastInfo({
          label: 'Select at least one resource',
          variant: 'error',
          open: true,
        })
      );
      return;
    }
    if (marks !== undefined && !/^\d+$/.test(marks.toString())) {
      dispatch(
        setToastInfo({
          label: 'Marks can be whole numbers only',
          variant: 'error',
          open: true,
        })
      );
      return;
    }
    try {
      setSaving(true);
      const savedId = await save(
        TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED
      );
      navigate(`${LEARNING_HOMEWORK_ASSIGN}?assessmentId=${savedId}`);
    } catch (err) {
      toastError(err, 'Could not save the homework');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async (resourceId: string, topicId?: number) => {
    try {
      // Save first: previewing leaves this page and unmounts the form.
      setSaving(true);
      const savedId = await save(
        TaskCreationStatusEnum.TASK_CREATION_STATUS_DRAFT
      );
      dispatch(
        setResourceRedirectionPath(
          `${location.pathname}?assessmentId=${savedId}`
        )
      );
      onResourceClick(
        navigate,
        {
          resourceId,
          subjectId: Number(subject_id),
          chapterId: Number(chapter_id),
          topicId,
        },
        SessionModeEnum.SESSION_MODE_RESOURCE
      );
    } catch (err) {
      toastError(err, 'Could not open the resource');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFunction = async (indexToDelete: number) => {
    const tempUserUploads = [...userUploads];
    tempUserUploads[indexToDelete].uploadFileData.isDeleted = true;
    setUserUploads(tempUserUploads);
  };

  const handleUploadDoc = async (filesToUpload: FileList) => {
    if (!filesToUpload || filesToUpload.length === 0) return;
    const existingFiles = new Set(
      userUploads
        .filter((file) => !file.uploadFileData?.isDeleted)
        .map((file) => `${file.uploadFileData.fileName}-${file.fileSize}`)
    );
    const newUploads: CustomUploadFileResponse[] = [];
    const filesForUploads: File[] = [];
    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];
      if (existingFiles.has(`${file.name}-${file.size}`)) {
        dispatch(
          setToastInfo({
            label: `Duplicate file not allowed : ${file.name}`,
            variant: 'error',
            open: true,
          })
        );
        continue;
      }
      newUploads.push({
        uploadFileData: new CloudUpload({
          gcpFileUrl: '',
          personId: teacher_id,
          fileSizeInMb: getFileSizeInMB(file.size),
          isDeleted: false,
          externalWebResourceUrl: '',
          fileName: file.name,
          fileType: getFileType(file.type),
          fileExtensionType: getFileExtensionType(file.type),
          isUploaded: false,
        }),
        fileUploadStatus: 'pending',
        fileSize: file.size,
      });
      filesForUploads.push(file);
    }
    if (newUploads.length === 0) return;
    setUserUploads((prev) => [...prev, ...newUploads]);

    // The signed upload URL needs a homework id, so create the draft first.
    let homeworkId = assessmentId;
    if (!homeworkId) {
      try {
        homeworkId = await save(
          TaskCreationStatusEnum.TASK_CREATION_STATUS_DRAFT
        );
      } catch (err) {
        console.error('creating draft failed:', err);
      }
    }

    for (let i = 0; i < newUploads.length; i++) {
      const upload = newUploads[i];
      const file = filesForUploads[i];
      if (getFileType(file.type) === FileEnum.FILE_TYPE_UNKNOWN) {
        upload.fileUploadStatus = 'Invalid Type';
        continue;
      }
      //! max file size to be 50 MB
      if (getFileSizeInMB(file.size) > 50) {
        upload.fileUploadStatus = 'Size Exceeded';
        continue;
      }
      if (!homeworkId) {
        upload.fileUploadStatus = 'Upload Error';
        continue;
      }
      try {
        const signedUrlResponse =
          await LmsCommonAPIServiceV2ClientWithStatusCodeHandler.getSignedUrlForFileUpload(
            {
              personId: teacher_id,
              filesInfo: [
                {
                  originalFilePath: file.name,
                  mimeType: getImageType(file.type),
                  size: getFileSizeInMB(file.size),
                },
              ],
              personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
              fileUploadPurpose: FileUploadPurpose.FILE_UPLOAD_FOR_HW_ASSET,
              sectionId: class_subject_info?.sectionId,
              subjectId: Number(subject_id),
              homeworkId,
            }
          );
        const signed = signedUrlResponse?.response?.[0]?.signedUrl;
        const uploadResult = signed
          ? await uploadFile({ file, signedUrl: getMediaBasePath(signed) })
          : undefined;
        if (signed && uploadResult?.success === true) {
          upload.uploadFileData.gcpFileUrl = signed;
          upload.uploadFileData.isUploaded = true;
          upload.fileUploadStatus = 'success';
        } else {
          upload.fileUploadStatus = 'Upload Error';
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        upload.fileUploadStatus = 'Upload Error';
      }
    }
    // The upload objects were mutated in place; publish a new array reference.
    setUserUploads((prev) => [...prev]);
  };

  if (restoring) return <Loader />;

  return (
    <Box sx={styles.root}>
      <Box sx={styles.header}>
        <Typography variant="h1" fontWeight={'medium'}>
          Create Learning Homework
        </Typography>
        <PrimaryButton
          sx={{
            width: { xs: '90%', md: pxTovW(397) },
            position: { xs: 'fixed', md: 'unset' },
            bottom: { xs: pxToRem(10) },
            left: { xs: '5%' },
            zIndex: { xs: 100, md: 0 },
          }}
          disabled={
            saving ||
            selectedResourceIds.length === 0 ||
            userUploads.some((item) => item.fileUploadStatus === 'pending')
          }
          onClick={handleProceedClick}
        >
          Proceed
        </PrimaryButton>
      </Box>

      <Box sx={styles.contentBox}>
        <Box sx={{ flex: 1 }}>
          <DragAndDropFiles
            handleDropFunction={(event: DragEvent<HTMLDivElement>) =>
              handleUploadDoc(event.dataTransfer.files)
            }
            handleChangeFunction={async (
              event: ChangeEvent<HTMLInputElement>
            ) => {
              await handleUploadDoc(event.target.files as FileList);
              // Reset so selecting the same file again triggers onChange
              if (event.target) event.target.value = '';
            }}
            handleDeleteFunction={handleDeleteFunction}
            userUploads={userUploads}
          />
          <Box sx={styles.marksBox}>
            <Typography variant={isMobile ? 'h2' : 'h3'}>Marks</Typography>
            <NumericalInputField
              variant="outlined"
              value={marks}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                if (Number(value) > 1000) return;
                setMarks(value ? Number(value) : undefined);
              }}
            />
          </Box>
          <FormControl sx={{ mt: pxToRem(10) }}>
            <FormLabel id="learning-hw-submission">
              <Typography variant="h3">Student Submission</Typography>
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="learning-hw-submission"
              value={uploadRequired ? 'upload' : 'resources_only'}
              onChange={(e) => setUploadRequired(e.target.value === 'upload')}
            >
              <FormControlLabel
                value="upload"
                control={<Radio />}
                label="Upload on App"
              />
              <FormControlLabel
                value="resources_only"
                control={<Radio />}
                label="Resources only"
              />
            </RadioGroup>
          </FormControl>
        </Box>
        <Box sx={{ flex: 1 }}>
          <InstructionsPanel
            isEditable
            value={instructionsValue}
            onChange={(e: string | undefined) => setInstructionsValue(e)}
          />
        </Box>
      </Box>

      <Box>
        <Typography
          variant="h2"
          sx={{ mb: { xs: pxToRem(10), md: pxTovW(16) } }}
        >
          Assign Resources ({selectedResourceIds.length} selected)
        </Typography>
        <Typography
          variant="bodyText"
          sx={{ display: 'block', mb: pxToRem(10) }}
        >
          Tick the resources students must complete. Ticking a lesson plan
          assigns all of its resources. Click a card to preview it.
        </Typography>
        <ResourcePicker
          subjectId={Number(subject_id)}
          chapterId={Number(chapter_id)}
          selectedResourceIds={selectedResourceIds}
          onChange={setSelectedResourceIds}
          onPreview={handlePreview}
        />
      </Box>
    </Box>
  );
};
